import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate, getFiltersGlobalFilters } from '@/utils/infrastructure';
import { CoreEnums } from '@/utils/domain/constants';
import CoreQueries from '@/utils/input/queries';

const api = bindServices('emailFormats');
const { BulkActionType } = CoreEnums;
const namespace = 'emailFormats';
const fetchRoute = 'emailNotificationTemplate';
const fetchParams = 'emailNotificationTemplate';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  expand: CoreQueries[fetchParams].paged.expand,
  searchSelect: CoreQueries[fetchParams].paged.searchSelect,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  filtersConfig: [],
};

const EmailFormatsModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...payload,
          pageSize: payload.limit ?? pageSize,
          page: payload.page ?? 1,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        const items = res?.items ?? res?.data ?? [];
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            items,
            limit: params.pageSize,
            page: params.page,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: {},
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: false });
        yield call(api.create, { route: fetchRoute, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, item: null, id: null, detail: false },
        });
        yield put({ type: 'fetchList', payload: payload.params || {} });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0]?.field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: false });
        yield call(api.update, { route: fetchRoute, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, item: null, id: null, detail: false },
        });
        yield put({ type: 'fetchList', payload: payload.params || {} });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0]?.field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, params } = payload;
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
        yield call(api.apiRoute, {
          route: `${fetchRoute}/${id}`,
          method: 'delete',
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, item: null, id: null, detail: false },
        });
        yield put({ type: 'fetchList', payload: params || {} });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
          yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
        }
      }
    },

    *bulkDeleteEmailFormats({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });

        for (const id of ids) {
          yield call(api.apiRoute, {
            route: `${fetchRoute}/${id}`,
            method: 'delete',
          });
        }

        const pageSize = yield select((state) => state.global.pageSize);
        const state = yield select((s) => s.emailFormats);
        const globalState = yield select((s) => s.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(state.orderBy && { orderBy: state.orderBy }),
          ...(filters && { filter: filters }),
          select: state.select,
          expand: state.expand,
          searchSelect: state.searchSelect,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { call, put }) {
      try {
        yield call(api.activate, {
          route: fetchRoute,
          id: payload.id,
          isActive: payload.state,
        });
        yield put({ type: 'editActives', payload });
      } catch (e) {
        /*  */
      }
    },

    *bulkActivateEmailFormats({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkEmailFormatsActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeactivateEmailFormats({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkEmailFormatsActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      const items = payload.items ?? payload.data ?? state.list;
      return {
        ...state,
        list: items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload?.state ?? payload?.showForm ?? false,
        detail: payload?.detail ?? false,
        id: payload?.id ?? null,
        item: payload?.item ?? null,
        success: false,
        validationErrors: [],
      };
    },

    queryItem(state, { payload }) {
      return { ...state, item: payload };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    editActives(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },

    bulkEmailFormatsActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    queryOrderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },

    setFiltersConfig(state, { payload }) {
      return { ...state, filtersConfig: payload ?? [] };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default EmailFormatsModel;
